import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import Konva from "konva"
import { Stage, Layer, Group, Arrow } from "react-konva"

import { IDLE, PENDING, EDIT } from "const"
import { LifecycleConnection, LifecyclePhase } from "server/models"
import {
    hasAccess,
    hasProjectAccess,
    hasLifecycleAccess,
} from "client/utils"
import {
    PRM_LIFECYCLE,
    PRM_LIFECYCLE_PHASE_LIST,
    StoreType,

    IsOwnerLifecycle,
    IsResponsibleLifecycle,
    LoadLifecyclePhases,
    LoadLifecycleConnections,

    setLifecyclePath,
    setLifecyclePhaseList,
    setLifecycleConnectionList,
    setLifecyclePhaseActive,
    setLifecycleConnectionActive,
    setLifecycleModelDrawConnection,
    setLifecycleModelLoadState,
    setLifecycleModelDragPhase,
} from "client/store"
import { Loader } from "client/components"

import { phaseWidth } from "./const"
import { GetPhaseGui, Side } from "./actions/phaseGui"
import { CalculateConnectionPoints, GetOrthogonalPoints } from "./actions/connectionPoints"
import { DragMovePhase, DragEndPhase } from "./actions/dragPhase"
import { StartDrawConnection, EndDrawConnection, MouseEnterPhase, StickTo, FollowMouse } from "./actions/drawConnection"
import AddNewPhase from "./actions/addPhase"

import { CanvasWrapper } from "../styled"

import {
    ConnectionArrow,
    PreviewConnectionOptions,
    PhaseRect,
    PhaseTitle,
    PhaseTitleHeight,
    InputDocs,
    OutputDocs,
    ChildMarker,
    GuiLine,
    GuiPoint,
} from "./styled"


let clickTimeStamp: number = 0
let dataWrapper: Konva.Layer

const Canvas = () => {
    const lifecycle = useSelector((state: StoreType) => state.lifecycle.lifecycleView)
    const lifecyclePath = useSelector((state: StoreType) => state.lifecycle.lifecyclePath)

    const phaseList = useSelector((state: StoreType) => state.lifecycle.model.phaseList)
    const connectionList = useSelector((state: StoreType) => state.lifecycle.model.connectionList)
    const activePhase = useSelector((state: StoreType) => state.lifecycle.model.activePhase)
    const activeConnection = useSelector((state: StoreType) => state.lifecycle.model.activeConnection)

    const dragPhase = useSelector((state: StoreType) => state.lifecycle.model.dragPhase)
    const drawConnection = useSelector((state: StoreType) => state.lifecycle.model.drawConnection)
    const reloadModel = useSelector((state: StoreType) => state.lifecycle.model.reloadModel)
    const loadState = useSelector((state: StoreType) => state.lifecycle.model.loadState)

    const onSelectPhase = useSelector((state: StoreType) => state.lifecycle.onSelectPhase)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    // stage settings
    const [width, setWidth] = useState<number>(1)
    const [height, setHeight] = useState<number>(1)
    const [scale, setScale] = useState<number>(1)
    const [positionX, setPositionX] = useState<number>(0)
    const [positionY, setPositionY] = useState<number>(0)

    const [activePoint, setActivePoint] = useState<{ x: number, y: number }>(null)

    const parentPhaseId = lifecyclePath && lifecyclePath.length
        ? lifecyclePath[lifecyclePath.length - 1].id
        : null


    const lifecycleEditAccess = !project.id
        ? hasAccess(PRM_LIFECYCLE, EDIT)
        : hasProjectAccess(PRM_LIFECYCLE, EDIT)

    const phaseListEditAccess = hasLifecycleAccess(PRM_LIFECYCLE_PHASE_LIST, EDIT)
    const isOwner = IsOwnerLifecycle(project.id, lifecycle, userData)
    const isResponsible = IsResponsibleLifecycle(lifecycle, userData)

    const editAccess = lifecycleEditAccess || isOwner || (project.id && isResponsible) || phaseListEditAccess


    useEffect(() => {
        resizeStage()
        window.addEventListener("resize", resizeStage)
        return () => window.removeEventListener("resize", resizeStage)
    }, [])

    useEffect(() => {
        if (!lifecycle || !lifecycle.id) return
        if (!reloadModel) return
        dispatch(setLifecycleModelLoadState(PENDING))

        LoadLifecyclePhases(lifecycle.id, parentPhaseId).then((phases) => {
            LoadLifecycleConnections(lifecycle.id, parentPhaseId).then((res) => {
                // fix connection points
                let connections: LifecycleConnection[] = res
                phases.forEach((phase) => {
                    const titleHeight = PhaseTitleHeight(phase.name)
                    const gui = GetPhaseGui(titleHeight)
                    connections = CalculateConnectionPoints(phase.id, phase.positionObject, gui, connections)
                })

                dispatch(setLifecyclePhaseList(phases))
                dispatch(setLifecycleConnectionList(connections))

                dispatch(setLifecycleModelLoadState(IDLE))
            })
        })
    }, [reloadModel, lifecycle, lifecyclePath])

    const resizeStage = () => {
        const wrapper = document.querySelector(CanvasWrapper) as HTMLElement
        if (wrapper) {
            const { clientWidth, clientHeight } = wrapper
            setWidth(clientWidth > 0 ? clientWidth : 1)
            setHeight(clientHeight > 0 ? clientHeight : 1)
        }
    }

    const dragStage = (e: Konva.KonvaEventObject<DragEvent>) => {
        const stage = e.target.getStage()
        setPositionX(stage.x())
        setPositionY(stage.y())
    }

    const scaleStage = (e: Konva.KonvaEventObject<WheelEvent>) => {
        if (dragPhase || drawConnection) return

        const scaleBy = 1.1
        const delta = e.evt.deltaY
        const stage = e.target.getStage()
        const pointer = stage.getPointerPosition()

        const mousePointTo = {
            x: (pointer.x - positionX) / scale,
            y: (pointer.y - positionY) / scale,
        }

        let newScale
        if (scale < 0.3 && delta > 0) {
            newScale = scale
        } else if (scale > 1.5 && delta < 0) {
            newScale = scale
        } else {
            newScale = delta < 0
                ? scale * scaleBy
                : scale / scaleBy
        }

        setScale(newScale)
        setPositionX(pointer.x - mousePointTo.x * newScale)
        setPositionY(pointer.y - mousePointTo.y * newScale)
    }

    const clickStage = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (e.evt.button !== 0) return

        const timeDiff = e.evt.timeStamp - clickTimeStamp
        clickTimeStamp = e.evt.timeStamp

        if (timeDiff < 300) {
            if (!editAccess) return

            AddNewPhase(
                dataWrapper,
                lifecycle.id,
                parentPhaseId,
                packageSettings,
                userData,
                lang,
                dispatch,
            )
        } else {
            dispatch(setLifecyclePhaseActive(null))
            dispatch(setLifecycleConnectionActive(null))
        }

        if (drawConnection) {
            dispatch(setLifecycleModelDrawConnection(null))
        }
    }

    const clickPhase = (
        e: Konva.KonvaEventObject<MouseEvent>,
        phase: LifecyclePhase,
    ) => {
        e.cancelBubble = true
        if (e.evt.button !== 0) return
        if (drawConnection) return

        const timeDiff = e.evt.timeStamp - clickTimeStamp
        clickTimeStamp = e.evt.timeStamp

        if (timeDiff > 300) {
            // move phase to top
            const newPhaseList = phaseList.filter((row) => row.id !== phase.id)
            newPhaseList.push(phase)

            dispatch(setLifecyclePhaseList(newPhaseList))
            dispatch(setLifecyclePhaseActive(phase))
            dispatch(setLifecycleConnectionActive(null))
        } else {
            dispatch(setLifecyclePhaseActive(null))
            dispatch(setLifecycleConnectionActive(null))

            const newLifecyclePath = lifecyclePath.slice()
            newLifecyclePath.push(phase)
            dispatch(setLifecyclePath(newLifecyclePath))
        }
    }

    const clickConnection = (
        e: Konva.KonvaEventObject<MouseEvent>,
        connection: LifecycleConnection,
    ) => {
        if (onSelectPhase) return

        if (e.evt.button !== 0) return
        if (drawConnection) return
        e.cancelBubble = true

        // move connection to top
        const newConnectionList = connectionList.filter((row) => row.id !== connection.id)
        newConnectionList.push(connection)

        dispatch(setLifecycleConnectionList(newConnectionList))
        dispatch(setLifecycleConnectionActive(connection))
        dispatch(setLifecyclePhaseActive(null))
    }


    // TODO: add modifying actions for connection points
    let connectionGuiPoints: { x: number, y: number }[] = []
    if (activeConnection && editAccess) {
        const { pointsArray } = activeConnection
        connectionGuiPoints = [
            {
                x: pointsArray[0],
                y: pointsArray[1],
            },
            {
                x: pointsArray[pointsArray.length - 2],
                y: pointsArray[pointsArray.length - 1],
            },
        ]
    }

    return (
        <CanvasWrapper onContextMenu={(e) => {
            e.stopPropagation()
            e.preventDefault()
        }}
        >
            {loadState === PENDING && <Loader paddingTop={250} />}

            <Stage
                width={width}
                height={height}
                x={positionX}
                y={positionY}
                scale={{ x: scale, y: scale }}
                draggable
                onClick={clickStage}
                onWheel={scaleStage}
                onDragEnd={dragStage}
                onMouseMove={() => {
                    FollowMouse(dataWrapper, drawConnection, dispatch)
                }}
            >
                <Layer ref={(node) => { dataWrapper = node }}>
                    {
                        connectionList.map((connection) => {
                            const {
                                id,
                                orthogonal,
                                pointsArray,
                                startPhaseSide,
                                endPhaseSide,
                                doubleEnd,
                            } = connection

                            const points: number[] = orthogonal
                                ? GetOrthogonalPoints(pointsArray, startPhaseSide as Side, endPhaseSide as Side)
                                : pointsArray.slice()

                            return (
                                <ConnectionArrow
                                    key={`arrow_${id}`}
                                    points={points}
                                    pointerAtBeginning={doubleEnd}
                                    active={activeConnection && activeConnection.id === id}
                                    onClick={(e) => clickConnection(e, connection)}
                                />
                            )
                        })
                    }

                    {
                        drawConnection
                        && <Arrow {...PreviewConnectionOptions(drawConnection.pointsArray.slice())} />
                    }

                    {
                        phaseList.map((phase) => {
                            const {
                                id,
                                name,
                                positionObject,
                                childPhases,
                                inputDocuments,
                                outputDocuments,
                            } = phase

                            const titleHeight = PhaseTitleHeight(name)
                            const gui = GetPhaseGui(titleHeight)

                            return (
                                <Group
                                    key={id}
                                    x={positionObject.x}
                                    y={positionObject.y}
                                    width={phaseWidth}
                                    height={titleHeight}
                                    draggable={editAccess && activePhase && activePhase.id === id && !drawConnection}
                                    onClick={(e) => clickPhase(e, phase)}
                                    onDragStart={(e) => {
                                        dispatch(setLifecycleModelDragPhase(e.currentTarget))
                                    }}
                                    onDragMove={() => {
                                        DragMovePhase(
                                            dataWrapper,
                                            dragPhase as Konva.Node,
                                            phase,
                                            gui,
                                            connectionList,
                                            dispatch,
                                        )
                                    }}
                                    onDragEnd={() => {
                                        DragEndPhase(
                                            dragPhase as Konva.Node,
                                            phase,
                                            dispatch,
                                        )
                                    }}
                                    onMouseEnter={() => {
                                        MouseEnterPhase(
                                            drawConnection,
                                            phase,
                                            dispatch,
                                        )
                                    }}
                                >
                                    <PhaseRect height={titleHeight} />
                                    <InputDocs
                                        height={titleHeight}
                                        text={`In\n${inputDocuments.length}`}
                                        visible={inputDocuments.length > 0}
                                    />
                                    <PhaseTitle text={name} />
                                    <OutputDocs
                                        childPhases={childPhases.length > 0}
                                        height={titleHeight}
                                        text={`Out\n${outputDocuments.length}`}
                                        visible={outputDocuments.length > 0}
                                    />

                                    <ChildMarker visible={childPhases.length > 0} />

                                    {
                                        // phase GUI
                                        activePhase && activePhase.id === id
                                        && Object.values(gui).map((sideGui) => {
                                            const { linePoints, x, y, side } = sideGui
                                            return ([
                                                <GuiLine
                                                    key="line"
                                                    points={linePoints}
                                                />,
                                                <GuiPoint
                                                    key="point"
                                                    x={x}
                                                    y={y}
                                                    active={
                                                        activePoint
                                                        && activePoint.x === x
                                                        && activePoint.y === y
                                                    }
                                                    visible={editAccess && !onSelectPhase}
                                                    onClick={(e) => (drawConnection
                                                        ? EndDrawConnection(
                                                            e,
                                                            drawConnection,
                                                            phase,
                                                            side,
                                                            dispatch,
                                                        ) : StartDrawConnection(
                                                            e,
                                                            dataWrapper,
                                                            lifecycle,
                                                            parentPhaseId,
                                                            phase,
                                                            side,
                                                            dispatch,
                                                        )
                                                    )}
                                                    onMouseEnter={(e) => {
                                                        setActivePoint({ x, y })
                                                        StickTo(
                                                            e,
                                                            dataWrapper,
                                                            drawConnection,
                                                            side,
                                                            dispatch,
                                                        )
                                                    }}
                                                    onMouseOut={() => setActivePoint(null)}
                                                    onMouseMove={(e) => { e.cancelBubble = true }}
                                                />,
                                            ])
                                        })
                                    }
                                </Group>
                            )
                        })
                    }

                    {
                        connectionGuiPoints.map((point) => (
                            <GuiPoint
                                key={`${point.x}_${point.y}`}
                                x={point.x}
                                y={point.y}
                                active={
                                    activePoint
                                    && activePoint.x === point.x
                                    && activePoint.y === point.y
                                }
                                visible
                                onClick={(e) => { e.cancelBubble = true }}
                                onMouseEnter={() => setActivePoint(point)}
                                onMouseOut={() => setActivePoint(null)}
                                onMouseMove={(e) => { e.cancelBubble = true }}
                            />
                        ))
                    }
                </Layer>
            </Stage>
        </CanvasWrapper>
    )
}
export default Canvas
